import { compactNumber } from "@/lib/formatNumber";
import { useGuard } from "@/lib/guards";
import { fmtElapsed } from "@/lib/liveTime";
import { toggleLiveFullscreen, isAnyFullscreen } from "@/lib/liveFullscreen"; // lot 762
import React, { useEffect, useRef, useState } from "react";
import { rich } from "@/lib/i18nRich";
import { useNavigate } from "react-router-dom";
import { prefersNativeHls, mseSupported, loadHls } from "@/lib/videoSource";
import { api, getToken } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveMediaUrl } from "@/components/FileUpload";
import { avatarSrc } from "@/lib/defaults";
import { liveTitle, fillName } from "@/lib/liveTitle";
import { offeringWording, roleLabelFr } from "@/lib/offeringLabel";
import { CURRENCIES, autoCurrency } from "@/lib/currencies";
import { Radio, Users, X, Heart, Send, Copy, Loader2, ShieldCheck, Wifi, Volume2, VolumeX, Maximize, Minimize, PlayCircle, Clock, Megaphone, Target, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { FloatingReactions, ReactionButtons, useFloatingReactions, emojisFromConfig } from "@/components/LiveReactions";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { BlockUserDialog } from "@/components/BlockUserDialog";
import { useBlockedIds, useUserBlocked } from "@/lib/blocks";
import { keepFocusTap } from "@/lib/keepFocusTap";
import { useScreenAwake } from "@/lib/screenAwake";
import { profileGateProps, requireCompleteProfile } from "@/lib/profileDone";
import { openLiveSocket } from "@/lib/liveSocket";
import { useVolumeKeyUnmute, VOLUME_PRIORITY } from "@/lib/volumeKeys";
import { liveSoundPref, saveLiveSoundPref, playWithAutoplayFallback } from "@/lib/liveSound";

const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");

// HLS video player (hls.js, with Safari native fallback). Used for OBS/RTMPS lives + replays.
// Hardened: a fresh live's HLS manifest 404s for the first ~10-20s, so we retry on
// fatal network errors, recover media errors, and rebuild on anything else — this
// prevents the permanent black screen the host used to see when going live via OBS.
export const HLS_STALL_BANNER_MS = 6000;
export const HLS_STALL_RELOAD_MS = 20000;

export const HlsPlayer = ({ src, muted = false, testId, className, controls = true, videoRef, poster, vod = false, signalLost = false, onAutoplayBlocked, compact = false }) => {
  const { t } = useApp();
  const innerRef = useRef(null);
  const ref = videoRef || innerRef;
  const [loading, setLoading] = useState(true);
  const [slow, setSlow] = useState(false);
  // Lot 600 — direct figé (l'hôte a perdu le réseau) : après 6 s sans image → bandeau « reprise automatique » ;
  // après 20 s → on force hls.js / Safari à recharger la playlist pour raccrocher le flux dès qu'il revient.
  const [stalled, setStalled] = useState(false);
  const reloadRef = useRef(null);
  // After ~12s still buffering (host RTMPS keeps reconnecting → no segments yet),
  // tell the viewer what's happening instead of an endless generic spinner.
  useEffect(() => {
    if (!loading) { setSlow(false); return undefined; }
    const st = setTimeout(() => setSlow(true), 12000);
    return () => clearTimeout(st);
  }, [loading]);
  useEffect(() => {
    const video = ref.current;
    if (!video || !src) return undefined;
    setLoading(true);
    let hls; let destroyed = false; let retryTimer; let stallTimer; let reloadTimer;
    const clearStall = () => { clearTimeout(stallTimer); clearTimeout(reloadTimer); stallTimer = null; reloadTimer = null; setStalled(false); };
    const onPlaying = () => { setLoading(false); clearStall(); };
    const onStall = () => {
      if (vod || destroyed || stallTimer) return;
      stallTimer = setTimeout(() => { if (!destroyed) setStalled(true); }, HLS_STALL_BANNER_MS);
      reloadTimer = setTimeout(() => { if (!destroyed) reloadRef.current?.(); }, HLS_STALL_RELOAD_MS);
    };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("waiting", onStall);
    video.addEventListener("stalled", onStall);
    // 1re image décodée (autoplay bloqué sans geste sur mobile web) : on retire le splash pour
    // laisser l'affiche + les contrôles natifs visibles au lieu d'un « Chargement… » sans fin.
    video.addEventListener("loadeddata", onPlaying);
    const playOrReveal = () => playWithAutoplayFallback(video, onAutoplayBlocked).catch(() => setLoading(false));

    // Replays (VOD) get instant startup like Reels: start at the LOWEST rendition
    // (near-instant first frame; ABR ramps quality up after 1-2s) and skip the
    // bandwidth probe. Live streams keep low-latency tuning to stay at the edge.
    const hlsConfig = vod
      ? {
          startLevel: 0,
          testBandwidth: false,
          startFragPrefetch: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          manifestLoadingMaxRetry: 12,
          manifestLoadingRetryDelay: 1500,
          manifestLoadingMaxRetryTimeout: 30000,
          levelLoadingMaxRetry: 10,
          fragLoadingMaxRetry: 10,
        }
      : {
          lowLatencyMode: true,
          liveSyncDurationCount: 2,
          backBufferLength: 15,
          manifestLoadingMaxRetry: 12,
          manifestLoadingRetryDelay: 1500,
          manifestLoadingMaxRetryTimeout: 30000,
          levelLoadingMaxRetry: 10,
          fragLoadingMaxRetry: 10,
        };

    if (prefersNativeHls(video)) {
      // Safari / iOS native HLS — retry the source if it errors early (manifest not ready yet).
      video.src = src;
      playOrReveal();
      const onErr = () => {
        if (destroyed) return;
        retryTimer = setTimeout(() => { if (!destroyed) { video.src = src; video.load(); playWithAutoplayFallback(video, onAutoplayBlocked).catch(() => {}); } }, 3000);
      };
      reloadRef.current = () => { try { video.src = src; video.load(); video.play?.().catch(() => {}); } catch { /* noop */ } };
      video.addEventListener("error", onErr);
      return () => { destroyed = true; clearTimeout(retryTimer); clearStall(); video.removeEventListener("playing", onPlaying); video.removeEventListener("loadeddata", onPlaying); video.removeEventListener("waiting", onStall); video.removeEventListener("stalled", onStall); video.removeEventListener("error", onErr); reloadRef.current = null; };
    }

    if (mseSupported()) {
      const build = (Hls) => {
        if (destroyed) return;
        hls = new Hls(hlsConfig);
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => { playOrReveal(); });
        reloadRef.current = () => { try { hls.stopLoad(); hls.startLoad(-1); } catch { try { hls.loadSource(src); } catch { /* noop */ } } };
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (destroyed || !data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            // Live manifest/segments not ready yet — keep trying.
            retryTimer = setTimeout(() => { if (!destroyed && hls) { try { hls.startLoad(); } catch { try { hls.loadSource(src); } catch { /* noop */ } } } }, 2000);
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            try { hls.recoverMediaError(); } catch { /* noop */ }
          } else {
            retryTimer = setTimeout(() => { if (destroyed) return; try { hls.destroy(); } catch { /* noop */ } build(Hls); }, 2500);
          }
        });
      };
      loadHls().then(build).catch(() => { if (!destroyed) { video.src = src; playOrReveal(); } });
    }
    return () => { destroyed = true; clearTimeout(retryTimer); clearStall(); video.removeEventListener("playing", onPlaying); video.removeEventListener("loadeddata", onPlaying); video.removeEventListener("waiting", onStall); video.removeEventListener("stalled", onStall); reloadRef.current = null; if (hls) { try { hls.destroy(); } catch { /* noop */ } } };
  }, [src, vod]);
  // Le serveur confirme la perte du signal (sondage / WS) → on relance la lecture dès que le flux est de retour.
  useEffect(() => {
    if (vod || !signalLost || loading) return undefined;
    const iv = setInterval(() => reloadRef.current?.(), HLS_STALL_RELOAD_MS);
    return () => clearInterval(iv);
  }, [signalLost, vod, loading]);
  const hostLost = !vod && !loading && (stalled || signalLost);
  return (
    <>
      <video ref={ref} autoPlay playsInline controls={controls} muted={muted} poster={poster || undefined} className={className || "w-full h-full object-contain absolute inset-0"} data-testid={testId} />
      {hostLost && (
        // Lot 739 — `compact` (carte du fil / profil, aperçu studio) : bandeau centré DANS la vidéo ; le décalage « 8,5 rem
        // sous la barre » est réservé à la salle plein écran (sinon il débordait sur « Rejoindre »).
        <div className="absolute inset-x-0 z-[7] flex justify-center px-3 pointer-events-none" style={compact ? { top: "50%", transform: "translateY(-50%)" } : { top: "calc(var(--sa-top, 0px) + 8.5rem)" }} data-testid="hls-host-lost" data-compact={compact ? "1" : "0"}>
          <span className={`flex items-center gap-2 rounded-2xl font-semibold shadow-lg text-center ${compact ? "px-3 py-1.5 text-[11px] max-w-[92%]" : "px-4 py-2 text-xs"}`} style={{ background: "rgba(180,83,9,0.92)", color: "#fff" }}>
            <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 animate-spin shrink-0" style={{ borderTopColor: "#fff" }} />
            {t("liveroom.host_signal_lost", "Connexion de l'hôte interrompue — reprise automatique…")}
          </span>
        </div>
      )}
      {loading && (
        // Opaque branded splash: covers the WebView's default grey play-button poster
        // (shown for the 1-5s before the HLS manifest/first frame is ready).
        <div className="absolute inset-0 grid place-items-center" data-testid="hls-loading" style={{ background: "#0B100E" }}>
          {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 blur-md" />}
          <div className="relative text-center text-white/85">
            {poster && <img src={poster} alt="" className="h-20 w-20 mx-auto rounded-full object-cover ring-2 ring-white/20 shadow-xl" />}
            <div className="h-9 w-9 mx-auto mt-3 rounded-full border-[3px] border-white/15 animate-spin" style={{ borderTopColor: "#E0A63C" }} />
            <p className="mt-2 text-xs">{vod ? t("liveroom.loading_replay", "Chargement de la rediffusion…") : (slow ? t("liveroom.host_unstable", "Connexion de l'hôte instable — le direct reprend dès que possible…") : t("liveroom.loading", "Chargement du direct…"))}</p>
          </div>
        </div>
      )}
    </>
  );
};

// Wait for ICE gathering to complete (trickle-less WHEP), max 2s.
const waitIce = (pc) => new Promise((res) => {
  if (pc.iceGatheringState === "complete") return res();
  const check = () => { if (pc.iceGatheringState === "complete") { pc.removeEventListener("icegatheringstatechange", check); res(); } };
  pc.addEventListener("icegatheringstatechange", check);
  setTimeout(res, 2000);
});

// WHEP (WebRTC) playback for browser-published (WHIP) lives — sub-second latency.
// A WHIP stream has NO HLS manifest, so viewers MUST use WHEP or they'd see a black screen.
export const WhepPlayer = ({ src, muted, onState, onDiag, testId, className, onAutoplayBlocked }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!src) return undefined;
    const video = ref.current;
    let pc; let resourceUrl; let retryTimer; let statsTimer; let attempt = 0; let cancelled = false;
    let lastBytes = 0; let lastTs = 0;
    const diag = { conn: "new", ice: "new", frames: 0, kbps: 0 };
    const emit = () => onDiag?.({ ...diag });
    onState?.("connecting");
    diag.conn = "connecting"; emit();

    const teardownPc = () => { try { pc?.close(); } catch { /* noop */ } };

    const startStats = () => {
      clearInterval(statsTimer);
      statsTimer = setInterval(async () => {
        if (!pc || cancelled) return;
        try {
          const stats = await pc.getStats();
          stats.forEach((r) => {
            if (r.type === "inbound-rtp" && (r.kind === "video" || r.mediaType === "video")) {
              diag.frames = r.framesDecoded ?? r.framesReceived ?? diag.frames;
              const now = r.timestamp || Date.now();
              if (lastTs && r.bytesReceived != null) {
                const db = r.bytesReceived - lastBytes;
                const dt = (now - lastTs) / 1000;
                if (dt > 0) diag.kbps = Math.max(0, Math.round((db * 8) / 1000 / dt));
              }
              lastBytes = r.bytesReceived ?? lastBytes; lastTs = now;
            }
          });
          emit();
        } catch { /* noop */ }
      }, 1000);
    };

    const scheduleRetry = () => {
      if (cancelled) return;
      teardownPc();
      clearInterval(statsTimer);
      const delay = Math.min(1000 * 2 ** attempt, 8000);
      attempt += 1;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => { if (!cancelled) connect(); }, delay);
    };

    const connect = async () => {
      const local = new RTCPeerConnection();  // Cloudflare examples use no custom STUN
      pc = local;
      diag.conn = "connecting"; diag.ice = local.iceConnectionState; emit();
      try {
        local.addTransceiver("video", { direction: "recvonly" });
        local.addTransceiver("audio", { direction: "recvonly" });
        const remote = new MediaStream();
        local.ontrack = (ev) => {
          // event.streams is not guaranteed on WHEP -> build the stream from tracks.
          if (!remote.getTracks().some((t) => t.id === ev.track.id)) remote.addTrack(ev.track);
          if (video) { video.srcObject = remote; playWithAutoplayFallback(video, onAutoplayBlocked).catch(() => {}); }
        };
        local.oniceconnectionstatechange = () => {
          if (cancelled || local !== pc) return;
          diag.ice = local.iceConnectionState; emit();
        };
        local.onconnectionstatechange = () => {
          if (cancelled || local !== pc) return;
          const st = local.connectionState;
          diag.conn = st; emit();
          if (st === "connected") { attempt = 0; onState?.("playing"); startStats(); }
          else if (["failed", "disconnected", "closed"].includes(st)) { onState?.("error"); scheduleRetry(); }
        };
        const offer = await local.createOffer();
        await local.setLocalDescription(offer);
        await waitIce(local);
        const resp = await fetch(src, { method: "POST", headers: { "Content-Type": "application/sdp" }, body: local.localDescription.sdp });
        const answer = await resp.text();
        if (!resp.ok || !answer.trim()) throw new Error(`WHEP ${resp.status}`);
        try { const loc = resp.headers.get("Location"); resourceUrl = loc ? new URL(loc, src).href : null; } catch { /* noop */ }
        if (cancelled) return;
        await local.setRemoteDescription({ type: "answer", sdp: answer });
      } catch (e) {
        if (!cancelled) { onState?.("error"); scheduleRetry(); }
      }
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      clearInterval(statsTimer);
      try { if (resourceUrl) fetch(resourceUrl, { method: "DELETE", keepalive: true }).catch(() => {}); } catch { /* noop */ }
      teardownPc();
    };
  }, [src]);  // eslint-disable-line react-hooks/exhaustive-deps
  return <video ref={ref} autoPlay playsInline muted={muted} className={className || "w-full h-full object-contain absolute inset-0"} data-testid={testId} />;
};

const CopyField = ({ label, value, testId }) => {
  const { t } = useApp();
  const copy = () => { navigator.clipboard.writeText(value || ""); toast.success(t("studio.copied", "{label} copié").replace("{label}", label)); };
  return (
    <div>
      <div className="text-xs font-semibold mb-1 text-white/60">{label}</div>
      <div className="flex items-center gap-2">
        <input readOnly value={value || ""} className="flex-1 min-w-0 h-9 rounded-lg px-3 text-xs font-mono truncate text-white" style={{ background: "#0A0D0C", border: "1px solid #273630" }} data-testid={testId} />
        <button onClick={copy} className="h-9 w-9 rounded-lg grid place-items-center shrink-0 text-white" style={{ background: "#209E76" }} data-testid={`${testId}-copy`}><Copy size={14} /></button>
      </div>
    </div>
  );
};

// Immersive dark "theater" live room — viewers watch WHEP (browser lives) or HLS
// (OBS lives / replays). Chat + reactions ride the existing WebSocket hub.
export const LiveRoom = ({ live, role, onClose, onDonate }) => {
  const nav = useNavigate();
  const { user, config, t } = useApp();
  const reactionEmojis = emojisFromConfig(config);
  const wsRef = useRef(null);
  const commentsEndRef = useRef(null);
  const [viewerCount, setViewerCount] = useState(live.viewers || 0);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const blockedIds = useBlockedIds();
  const [blockTarget, setBlockTarget] = useState(null);
  // A block made during the live: drop that member's lines and tell the room socket.
  useUserBlocked((id) => {
    if (!id) return;
    setComments((c) => c.filter((x) => x.userId !== id));
    wsRef.current?.send({ type: "block", user_id: id });
  });
  const [floats, pushFloat] = useFloatingReactions();
  // Lot 668 — son activé par défaut (mémoire : le spectateur qui coupe le son le retrouve coupé) ; si le navigateur
  // refuse le démarrage avec son (aucun geste), le lecteur bascule en muet et la pastille « Activer le son » apparaît.
  const [muted, setMuted] = useState(() => role === "host" || !liveSoundPref());
  const onAutoplayBlocked = () => setMuted(true);
  const setSound = (on) => { setMuted(!on); if (role !== "host") saveLiveSoundPref(on); };
  // Lot 638 — spectateur : touche de volume physique → son du direct activé (comme « Activer le son »).
  useVolumeKeyUnmute(role !== "host", VOLUME_PRIORITY.LIVE, () => setSound(true));
  const [whepState, setWhepState] = useState("connecting");
  const [pb, setPb] = useState({
    status: live.status || "waiting",
    is_live: !!live.is_live,
    hls_url: live.hls_url || null,
    whep_url: live.whep_url || null,
    ingest: live.ingest || null,
    replay_url: live.replay_url || null,
    rtmps_url: live.rtmps_url || null,
    rtmps_key: live.rtmps_key || null,
    accept_offerings: !!live.accept_offerings,
    offering_total: Number(live.offering_total || 0),
    offering_goal: live.offering_goal || null,
  });
  const [ending, setEnding] = useState(false);
  const [endConfirm, setEndConfirm] = useState(false); // styled in-app "end live?" dialog (no native window.confirm)
  const [redirectIn, setRedirectIn] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const fsStageOn = useGuard("live_fullscreen_stage"); // lot 762
  const fsCommentsOn = useGuard("live_fullscreen_comments"); // lot 763 — colonne de commentaires repliable en plein écran
  // Lot 763 — avec la colonne de commentaires, le plein écran porte sur la salle (vidéo + dock) ; la grille passe en
  // « vidéo + colonne 320 px » en paysage (CSS [data-fs]) ; la colonne se replie / se rouvre sans quitter le plein écran.
  const fsWithComments = fsCommentsOn && role !== "host";
  const [fsChatOpen, setFsChatOpen] = useState(true);
  const [fsUnread, setFsUnread] = useState(0);
  const fsRef = useRef({ fs: false, chatOpen: true });
  fsRef.current = { fs: isFs, chatOpen: fsChatOpen };
  const toggleFullscreen = () => toggleLiveFullscreen(stageRef.current, rootRef.current, { stageMode: fsStageOn && !fsWithComments });
  useEffect(() => {
    const onFs = () => { const on = isAnyFullscreen(); setIsFs(on); if (!on) { setFsChatOpen(true); setFsUnread(0); } };
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => { document.removeEventListener("fullscreenchange", onFs); document.removeEventListener("webkitfullscreenchange", onFs); };
  }, []);
  const openFsChat = () => { setFsChatOpen(true); setFsUnread(0); };

  const isHost = role === "host";
  // The OWNER (strictly the host of THIS live) can ALWAYS end their own live, even when
  // routed as a plain viewer (a WHIP live reopened from the public link after a refresh,
  // power cut or on a second device). NOTE: super_admins are deliberately NOT owners of
  // every live — they watch as normal viewers (with the Soutenir/Faire un don buttons).
  const isOwner = !!user && live.host_id === user?.id;
  const canEnd = isHost || isOwner;
  const send = (msg) => { wsRef.current?.send(msg); };
  const sendReaction = (emoji) => send({ type: "reaction", emoji });
  const tapReact = () => { sendReaction("❤️"); pushFloat("❤️"); };

  // Animated on-screen thank-you when an offering is received (host + all viewers see it).
  const [offering, setOffering] = useState(null);
  const offeringTimerRef = useRef(null);
  const showOffering = (m) => {
    setOffering({ id: Date.now(), donor: m.donor_name, avatar: m.donor_avatar, amount: m.amount, currency: m.currency, message: m.message });
    // Cagnotte live: raise the goal bar instantly (fall back to incrementing by the
    // received amount if the server didn't send the running total).
    setPb((prev) => ({
      ...prev,
      offering_total: m.offering_total != null ? Number(m.offering_total) : Number(prev.offering_total || 0) + Number(m.amount || 0),
      offering_goal: m.offering_goal != null ? Number(m.offering_goal) : prev.offering_goal,
    }));
    if (offeringTimerRef.current) clearTimeout(offeringTimerRef.current);
    offeringTimerRef.current = setTimeout(() => setOffering(null), 6500);
  };
  const [tapHint, setTapHint] = useState(true);
  useEffect(() => { const tm = setTimeout(() => setTapHint(false), 5000); return () => clearTimeout(tm); }, []);

  // Lot 600 — socket de la salle avec reconnexion automatique (coupure réseau du spectateur ou de l'hôte).
  useEffect(() => {
    const sock = openLiveSocket(() => `${WS_BASE}/api/ws/live/${live.id}?token=${getToken()}&role=${role}`, {
      onMessage: (msg) => {
        if (msg.type === "viewer-count") setViewerCount(msg.count);
        else if (msg.type === "reaction") pushFloat(msg.emoji);
        else if (msg.type === "chat") {
          setComments((c) => [...c.slice(-99), { id: Date.now() + Math.random(), userId: msg.user_id, name: msg.name, text: msg.text, isHost: msg.is_host }]);
          if (fsRef.current.fs && !fsRef.current.chatOpen) setFsUnread((n) => Math.min(99, n + 1)); // lot 763 — colonne repliée
        }
        else if (msg.type === "error" && msg.code === "profile_incomplete") requireCompleteProfile({ profile_completion: { complete: false } });
        else if (msg.type === "ended") setPb((prev) => ({ ...prev, status: "ended", is_live: false, replay_url: msg.replay_url ?? prev.replay_url }));
        else if (msg.type === "signal") setPb((prev) => ({ ...prev, signal_lost: !!msg.lost }));
        else if (msg.type === "offering") showOffering(msg);
      },
    });
    wsRef.current = sock;
    return () => { sock.close(); wsRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.id, role]);
  const prevLostRef = useRef(false);
  useEffect(() => {
    if (prevLostRef.current && !pb.signal_lost && pb.status === "live") toast.success(t("liveroom.host_signal_back", "Le direct a repris ✅"));
    prevLostRef.current = !!pb.signal_lost;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pb.signal_lost]);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/${live.id}/playback`);
        if (!stop) setPb((prev) => ({ ...prev, ...data }));
      } catch { /* noop */ }
    };
    poll();
    const iv = setInterval(() => { if (pb.status !== "ended") poll(); }, 5000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.id]);

  useEffect(() => { commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [comments]);

  // When the host ends the live (and no replay is available yet), send viewers back
  // to the feed automatically after a visible countdown.
  useEffect(() => {
    if (isHost || pb.status !== "ended" || pb.replay_url) { setRedirectIn(null); return undefined; }
    setRedirectIn(5);
    const iv = setInterval(() => setRedirectIn((n) => (n == null ? null : Math.max(0, n - 1))), 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pb.status, pb.replay_url, isHost]);

  useEffect(() => {
    if (redirectIn === 0) onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectIn]);

  const sendComment = (text) => {
    const t = (text ?? commentText).trim();
    if (!t) return;
    send({ type: "chat", text: t });
    setCommentText("");
  };

  const endLive = async () => {
    if (canEnd) {
      setEnding(true);
      try { await api.post(`/live/${live.id}/stop`); toast.success(t("studio.ended_toast", "Direct terminé 🎬")); } catch { /* noop */ }
      setEnding(false);
    }
    onClose?.();
  };

  const handleClose = () => {
    // Host / owner ending the broadcast → styled in-app confirmation (NOT the native
    // window.confirm, which looks like a raw "yalola.app indique" Android dialog).
    // Viewers just leave.
    if (canEnd) { setEndConfirm(true); return; }
    endLive();
  };

  const canOffer = pb.accept_offerings;
  const showOfferingCta = isHost && !canOffer;
  // The CTA wording adapts to the role the (unverified) host is aiming for.
  const ctaReqRole = ["preacher", "artist", "influencer"].includes(user?.requested_role) ? user.requested_role : null;
  const ctaOffer = offeringWording(ctaReqRole, t);
  const ctaRoleLabel = roleLabelFr(ctaReqRole, t);

  // Playback selection: WHEP for browser (WHIP) lives, HLS for OBS lives, HLS for replays.
  const isLiveNow = pb.status === "live" && pb.is_live;
  // Lot 594 — spectateur : écran maintenu allumé tant que le direct est en cours (même son coupé).
  useScreenAwake(isLiveNow, "live-viewer");
  const useWhep = isLiveNow && pb.ingest === "webrtc" && !!pb.whep_url;
  const useHls = isLiveNow && !useWhep && !!pb.hls_url;
  const showReplay = pb.status === "ended" && !!pb.replay_url;
  const showUnmute = (useWhep || useHls) && muted && !isHost;

  // Live time-limit countdown (configured in Admin → Directs). At the limit the
  // stream is auto-stopped server-side; the host is warned 5 min before.
  const maxMin = pb.max_duration_min || 0;
  const liveStartMs = pb.live_started_at ? new Date(pb.live_started_at).getTime() : null;
  const hasLimit = isLiveNow && maxMin > 0 && !!liveStartMs;
  const remainingSec = hasLimit ? Math.max(0, Math.round(maxMin * 60 - (now - liveStartMs) / 1000)) : null;
  const timeUp = hasLimit && remainingSec <= 0;
  const lowTime = hasLimit && remainingSec > 0 && remainingSec <= 120;
  const warn5 = hasLimit && remainingSec > 0 && remainingSec <= 300;
  const fmtMMSS = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const hostOnlyCountdown = useGuard("live_countdown_host_only"); // lot 720
  // Lot 721 — spectateurs : durée écoulée (« En direct depuis 12 min ») à la place du compte à rebours réservé à l'hôte.
  const elapsedSec = isLiveNow && liveStartMs ? Math.max(0, Math.round((now - liveStartMs) / 1000)) : null;
  const host = pb.host || live.host;
  const offer = offeringWording(host?.role, t);
  const showGoal = (config?.settings?.features?.offering_goal) !== false;
  const goalCur = autoCurrency({ userCountry: host?.country, preferredCurrency: host?.preferred_currency, defaultCurrency: config?.settings?.default_currency });
  const goalSym = (CURRENCIES.find((c) => c.code === goalCur) || {}).symbol || goalCur;
  const churchLogo = live.church_logo_url || pb.church_logo_url;
  const churchName = live.church_name || pb.church_name;
  const churchId = live.church_id || pb.church_id;
  const liveBadgeOn = config?.settings?.church?.live_badge !== false;
  const showChurchBadge = !!(churchName && churchId && liveBadgeOn);
  const goChurch = () => { onClose?.(); nav(`/churches/${churchId}`); };
  const identityName = churchName || host?.name;
  const identityAvatar = churchLogo ? resolveMediaUrl(churchLogo) : avatarSrc(host);
  // Poster shown while the stream buffers: the live's 16:9 thumbnail, else church logo/host avatar.
  const posterUrl = (pb.poster_url ? resolveMediaUrl(pb.poster_url) : null) || (live.thumbnail_url ? resolveMediaUrl(live.thumbnail_url) : null) || identityAvatar;

  return (
    <div ref={rootRef} className="fixed inset-0 z-[60] text-white" style={{ background: "#0B100E", fontFamily: "Outfit, sans-serif" }} data-testid="live-room" data-fs={isFs ? "1" : undefined} data-fs-chat={isFs && fsWithComments ? (fsChatOpen ? "1" : "0") : undefined}>
      <div className="live-room-grid grid grid-cols-1 lg:grid-cols-[1fr_384px] h-full w-full">
        {/* ---------------- Stage ---------------- */}
        <div ref={stageRef} className="live-stage relative bg-black min-h-[42vh] lg:min-h-0 overflow-hidden group" data-testid="live-stage">
          {useWhep ? (
            <WhepPlayer src={pb.whep_url} muted={muted} onState={setWhepState} onAutoplayBlocked={onAutoplayBlocked} testId="live-video" />
          ) : useHls ? (
            <HlsPlayer src={pb.hls_url} muted={isHost || muted} controls={false} poster={posterUrl} testId="live-video" signalLost={!!pb.signal_lost} onAutoplayBlocked={onAutoplayBlocked} />
          ) : showReplay ? (
            <HlsPlayer src={pb.replay_url} poster={posterUrl} testId="live-replay-video" vod />
          ) : null}

          {/* Tap-to-react: touch anywhere on the stage to fire a floating ❤️ (viewers). */}
          {(useWhep || useHls) && !isHost && (
            <div className="absolute inset-0 z-[5] cursor-pointer" onClick={tapReact} data-testid="live-tap-react" title={t("liveroom.tap_heart", "Touchez pour envoyer un ❤️")} />
          )}

          {/* Host OBS instructions (only if a host somehow uses this room) */}
          {isHost && !isLiveNow && pb.status !== "ended" && (
            <div className="absolute inset-0 overflow-y-auto p-6" data-testid="live-obs-guide" style={{ background: "linear-gradient(180deg,#0B100E,#131C18)" }}>
              <div className="max-w-md mx-auto space-y-4">
                <div className="text-center">
                  <Wifi size={34} className="mx-auto text-white/60" />
                  <h3 className="mt-2 text-2xl font-black tracking-tight">{t("liveroom.how_to_title", "Comment diffuser")}</h3>
                  <p className="text-sm text-white/60 mt-1">{rich(t("liveroom.how_to_body", "Ouvre {obs}, colle ces 2 valeurs dans {settings}, puis clique « Démarrer le streaming »."), { obs: <b>OBS Studio</b>, settings: <b>{t("liveroom.obs_settings_path", "Réglages → Diffusion")}</b> })}</p>
                </div>
                <div className="space-y-3 rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid #273630" }}>
                  <CopyField label={t("studio.server_url", "Serveur (URL RTMPS)")} value={pb.rtmps_url} testId="live-rtmps-url" />
                  <CopyField label={t("studio.stream_key", "Clé de stream")} value={pb.rtmps_key} testId="live-rtmps-key" />
                  <p className="text-[11px] text-white/40">{t("studio.never_share_key", "Ne partage jamais ta clé de stream.")}</p>
                </div>
                <div className="rounded-2xl p-3 text-[12px] leading-relaxed text-white/70"
                     style={{ background: "rgba(224,166,60,0.08)", border: "1px solid rgba(224,166,60,0.25)" }}
                     data-testid="live-latency-note">
                  <b className="text-[#E0A63C]">{t("liveroom.latency_title", "Latence normale : 10 à 30 secondes.")}</b> {rich(t("liveroom.latency_body", "La diffusion OBS utilise le protocole {hls}, qui met le direct en mémoire tampon pour offrir une image stable et fluide à tous — même sur une connexion lente. Ce léger décalage est normal et n'affecte en rien la qualité de ton culte ou de ta prédication. (La caméra intégrée du navigateur, en WebRTC, est quasi instantanée mais moins robuste pour un large public.)"), { hls: <b>HLS</b> })}
                </div>
                <details className="rounded-2xl overflow-hidden"
                         style={{ background: "rgba(255,255,255,0.05)", border: "1px solid #273630" }}
                         data-testid="live-obs-mini-guide">
                  <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold flex items-center gap-2 text-white/85">
                    <PlayCircle size={16} className="text-[#28C291]" /> {t("liveroom.guide_title", "Configurer OBS en 2 minutes")}
                  </summary>
                  <ol className="px-5 pb-4 pt-1 space-y-2 text-[12.5px] text-white/70 list-decimal list-inside">
                    <li>{rich(t("liveroom.guide_1", "Télécharge et installe {obs} (gratuit — Windows, Mac, Linux)."), { obs: <a href="https://obsproject.com/download" target="_blank" rel="noreferrer" className="underline text-[#E0A63C]">OBS Studio</a> })}</li>
                    <li>{rich(t("liveroom.guide_2", "Ouvre {settings} et choisis le service {custom}."), { settings: <b>{t("liveroom.obs_settings_path", "Réglages → Diffusion")}</b>, custom: <b>{t("liveroom.guide_custom", "« Personnalisé… »")}</b> })}</li>
                    <li>{rich(t("liveroom.guide_3", "Colle l'{url} ci-dessus dans « Serveur » et la {key} dans « Clé de stream », puis valide."), { url: <b>{t("liveroom.guide_url", "URL RTMPS")}</b>, key: <b>{t("studio.stream_key", "Clé de stream")}</b> })}</li>
                    <li>{rich(t("liveroom.guide_4", "Dans {sources}, ajoute ta caméra ({cam}) et/ou ton écran ({screen})."), { sources: <b>{t("liveroom.guide_sources", "Sources")}</b>, cam: <i>{t("liveroom.guide_cam", "Périphérique de capture vidéo")}</i>, screen: <i>{t("liveroom.guide_screen", "Capture d'écran")}</i> })}</li>
                    <li>{rich(t("liveroom.guide_5", "Clique {start}, puis reviens ici : le direct s'affiche en quelques secondes."), { start: <b>{t("liveroom.guide_start", "« Démarrer le streaming »")}</b> })}</li>
                  </ol>
                </details>
                <div className="flex items-center justify-center gap-2 text-sm text-[#E0A63C]" data-testid="live-status-waiting">
                  <Loader2 size={16} className="animate-spin" /> {t("liveroom.waiting_feed", "En attente du flux…")}
                </div>
              </div>
            </div>
          )}

          {/* Viewer waiting / connecting screen */}
          {!isHost && !showReplay && (!isLiveNow || ((useWhep) && whepState !== "playing")) && (
            <div className="absolute inset-0 grid place-items-center" data-testid="live-viewer-waiting" style={{ background: "radial-gradient(120% 120% at 50% 0%, #16241D 0%, #0B100E 70%)" }}>
              <div className="text-center px-6">
                <div className="h-16 w-16 mx-auto rounded-full grid place-items-center animate-pulse" style={{ background: "rgba(32,158,118,0.15)", border: "1px solid rgba(32,158,118,0.4)" }}>
                  <Radio size={28} className="text-[#28C291]" />
                </div>
                <p className="mt-4 text-white/70">
                  {!isLiveNow ? t("liveroom.starting_soon", "Le direct va bientôt commencer…") : t("liveroom.connecting", "Connexion au direct…")}
                </p>
              </div>
            </div>
          )}

          {/* Top gradient (YouTube-style header) — extends behind the status bar */}
          <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent z-[8]" style={{ height: "calc(8rem + var(--sa-top))" }} />
          <div className="absolute top-0 left-0 right-0 z-10 flex items-start justify-between gap-3 p-4" style={{ paddingTop: "calc(1rem + var(--sa-top))" }} data-testid="live-header">
            <div className="min-w-0 flex items-center gap-2.5">
              <Avatar className="h-10 w-10 ring-2 ring-white/25 shrink-0">
                <AvatarImage src={identityAvatar} />
                <AvatarFallback>{identityName?.[0] || "?"}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {isLiveNow && (
                    <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: "#EF4444", color: "white" }} data-testid="live-badge-live">
                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> {t("liveroom.badge_live", "EN DIRECT")}
                    </span>
                  )}
                  {showReplay && (
                    <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide" style={{ background: "rgba(0,0,0,0.6)", color: "white" }} data-testid="live-badge-replay">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" /> Replay
                    </span>
                  )}
                  {showChurchBadge && (
                    <button
                      type="button"
                      onClick={goChurch}
                      className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded max-w-[180px] pointer-events-auto hover:opacity-90 transition-opacity"
                      style={{ background: "rgba(45,90,76,0.9)", color: "white", backdropFilter: "blur(8px)" }}
                      data-testid="live-church-badge"
                      title={churchName}
                    >
                      <Megaphone size={11} className="shrink-0" />
                      <span className="truncate">{fillName(t(showReplay ? "live.title_replay" : "live.title_live", showReplay ? "{name} était en direct" : "{name} est en direct"), churchName)}</span>
                    </button>
                  )}
                </div>
                <h1 className="text-base lg:text-xl font-black tracking-tight truncate drop-shadow leading-tight mt-0.5" data-testid="live-title">{liveTitle(live, t, { name: host?.name || identityName, ended: showReplay || pb.status === "ended" || live.status === "ended" })}</h1>
                {identityName && (
                  <span className="text-xs font-medium text-white/85 truncate flex items-center gap-1" data-testid="live-host-name">
                    {identityName}
                    {churchName ? <ShieldCheck size={12} style={{ color: "#7fdcb0" }} /> : <VerifiedBadge user={host} size={12} />}
                    {churchName && host?.name ? ` · par ${host.name}` : ""}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.1)", color: "white" }} data-testid="live-viewer-count">
                <Users size={13} /> {compactNumber(viewerCount)}
              </span>
              {hasLimit && (!hostOnlyCountdown || isHost) && (
                <span
                  className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md"
                  style={{ background: (timeUp || lowTime) ? "#EF4444" : "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)", color: "white" }}
                  data-testid="live-countdown"
                  title={t("liveroom.suggested_duration", "Durée conseillée du direct")}
                >
                  {/* Lot 720 — garde-fou live_countdown_host_only : hôte seul + « Reste 58:37 » (OFF = chiffre brut pour tous, comme avant). */}
                  <Clock size={13} /> {timeUp ? t("studio.time_up", "Temps écoulé") : hostOnlyCountdown ? t("liveroom.time_left", "Reste {t}").replace("{t}", fmtMMSS(remainingSec)) : fmtMMSS(remainingSec)}
                </span>
              )}
              {hostOnlyCountdown && !isHost && elapsedSec !== null && (
                <span
                  className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md"
                  style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)", color: "white" }}
                  data-testid="live-elapsed"
                >
                  <Clock size={13} /> {t("liveroom.live_since", "En direct depuis {t}").replace("{t}", fmtElapsed(elapsedSec))}
                </span>
              )}
            </div>
          </div>
          {warn5 && isHost && !timeUp && (
            <div
              className="absolute top-24 left-1/2 -translate-x-1/2 z-20 max-w-[90%] text-center px-4 py-2 rounded-xl text-[12px] font-semibold"
              style={{ background: "rgba(239,68,68,0.95)", color: "white" }}
              data-testid="live-autostop-warning"
            >
              ⚠️ Arrêt automatique dans {fmtMMSS(remainingSec)} (durée maximale atteinte). Pense à conclure ton direct 🙏
            </div>
          )}
          {timeUp && isHost && (
            <div
              className="absolute top-24 left-1/2 -translate-x-1/2 z-20 max-w-[88%] text-center px-4 py-2 rounded-xl text-[12px] font-semibold"
              style={{ background: "rgba(239,68,68,0.94)", color: "white" }}
              data-testid="live-time-up-banner"
            >
              {t("liveroom.time_up_banner", "Durée maximale atteinte — ton direct va s'arrêter automatiquement 🙏")}
            </div>
          )}

          {/* Unmute pill */}
          {showUnmute && (
            <button
              onClick={() => setSound(true)}
              className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-transform duration-300 hover:scale-105"
              style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.15)" }}
              data-testid="live-unmute-btn"
            >
              <VolumeX size={16} /> {t("liveroom.unmute", "Activer le son")}
            </button>
          )}
          {!showUnmute && (useWhep || useHls) && !isHost && (
            <button
              onClick={() => setSound(muted)}
              className="absolute bottom-24 right-5 z-20 h-10 w-10 grid place-items-center rounded-full transition-transform duration-300 hover:scale-110"
              style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)" }}
              data-testid="live-mute-toggle"
            >
              {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </button>
          )}
          {(useWhep || useHls) && !isHost && (
            <button
              onClick={toggleFullscreen}
              className="absolute bottom-[152px] right-5 z-20 h-10 w-10 grid place-items-center rounded-full transition-transform duration-300 hover:scale-110"
              style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.12)" }}
              data-testid="live-fullscreen-toggle"
              title={isFs ? t("liveroom.exit_fullscreen", "Quitter le plein écran") : t("liveroom.fullscreen", "Plein écran")}
            >
              {isFs ? <Minimize size={17} /> : <Maximize size={17} />}
            </button>
          )}

          <FloatingReactions floats={floats} />

          {/* Lot 763 — plein écran, colonne repliée : pastille à droite pour rouvrir les commentaires (badge = messages manqués) */}
          {isFs && fsWithComments && !fsChatOpen && (
            <button
              type="button"
              onClick={openFsChat}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1.5 pl-3 pr-2.5 py-2.5 rounded-l-full text-xs font-semibold transition-transform duration-300 hover:scale-105"
              style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.14)", borderRight: 0 }}
              title={t("liveroom.fs_chat_show", "Afficher les commentaires")}
              data-testid="live-fs-chat-open"
            >
              <ChevronLeft size={14} /> <MessageSquare size={15} />
              {fsUnread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: "#EF4444" }} data-testid="live-fs-chat-unread">{fsUnread}</span>}
            </button>
          )}

          {offering && (
            <div key={offering.id} className="pointer-events-none absolute left-1/2 -translate-x-1/2 z-[9] px-4 w-full max-w-sm" style={{ top: "calc(var(--sa-top) + 4.2rem)" }} data-testid="live-offering-toast">
              <div className="yalola-offering-pop flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl" style={{ background: "linear-gradient(100deg,#B8860B,#2D5A4C)", border: "1px solid rgba(255,255,255,0.25)" }}>
                <Avatar className="h-9 w-9 ring-2 ring-white/40">
                  <AvatarImage src={avatarSrc(offering.avatar)} />
                  <AvatarFallback>{(offering.donor || "?").slice(0, 1)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-sm font-bold flex items-center gap-1 truncate"><Heart size={14} className="shrink-0" fill="#fff" /> {offering.donor}</div>
                  <div className="text-xs opacity-95 truncate">{t("liveroom.offered", "a offert")} <b>{offering.amount} {offering.currency}</b> 🙏{offering.message ? ` — “${offering.message}”` : ""}</div>
                </div>
              </div>
            </div>
          )}
          {tapHint && (useWhep || useHls) && !isHost && !showUnmute && (
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-[6] px-3 py-1.5 rounded-full text-xs pointer-events-none animate-pulse" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)" }} data-testid="live-tap-hint">
              {t("liveroom.tap_hint", "Touchez l'écran pour réagir ❤️")}
            </div>
          )}
          {pb.status !== "ended" && <ReactionButtons onSend={sendReaction} emojis={reactionEmojis} />}

          {/* Host + title now shown in the top header (YouTube-style) */}

          {pb.status === "ended" && !showReplay && (
            <div className="absolute inset-0 grid place-items-center bg-black/85 z-10" data-testid="live-ended-screen">
              <div className="text-center px-6">
                <Radio size={40} className="mx-auto text-white/50" />
                <p className="mt-3 font-semibold" data-testid="live-ended-msg">{t("liveroom.ended_msg", "Le direct est terminé.")}</p>
                {!isHost && (
                  <div className="mt-3 flex flex-col items-center gap-2">
                    <p className="text-sm text-white/60" data-testid="live-ended-countdown">
                      {t("liveroom.redirect_home", "Redirection vers l'accueil")}{redirectIn != null ? t("liveroom.redirect_in", " dans {n}…").replace("{n}", redirectIn) : "\u2026"}
                    </p>
                    <Button onClick={() => onClose?.()} size="sm" className="rounded-full bg-white/15 hover:bg-white/25 text-white" data-testid="live-ended-home-btn">
                      {t("liveroom.back_home", "Retour à l'accueil")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ---------------- Chat dock (glassmorphism) ---------------- */}
        <div className="live-chat-dock flex flex-col min-h-0 border-t lg:border-t-0 lg:border-l" style={{ background: "rgba(19,28,24,0.72)", backdropFilter: "blur(18px)", borderColor: "rgba(255,255,255,0.08)" }} data-testid="live-chat-dock">
          <div className="px-4 py-3 flex items-center justify-between gap-2 border-b shrink-0 flex-wrap" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-white/60 truncate min-w-0 flex-1">{t("studio.live_comments", "Commentaires en direct")}</div>
            <div className="flex items-center gap-1.5 shrink-0">
              {isFs && fsWithComments && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-full text-white/80 hover:text-white hover:bg-white/10 whitespace-nowrap"
                  onClick={() => setFsChatOpen(false)}
                  title={t("liveroom.fs_chat_hide", "Replier les commentaires")}
                  data-testid="live-fs-chat-collapse"
                >
                  <ChevronRight size={15} className="mr-1" /> {t("liveroom.fs_chat_hide_short", "Replier")}
                </Button>
              )}
              {isHost && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-full text-white/80 hover:text-white hover:bg-white/10 whitespace-nowrap"
                  onClick={() => nav(`/live?create=1&resume=${live.id}`)}
                  data-testid="live-room-open-studio"
                >
                  <Radio size={15} className="mr-1" /> Studio
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-full text-white/80 hover:text-white hover:bg-white/10 whitespace-nowrap"
                onClick={handleClose}
                disabled={ending}
                data-testid="live-room-close"
              >
                {ending ? <Loader2 size={15} className="mr-1 animate-spin" /> : <X size={15} className="mr-1" />} {canEnd ? t("liveroom.end", "Terminer") : t("liveroom.leave", "Quitter")}
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3.5 space-y-2.5 min-h-[140px]" data-testid="live-comments">
            {comments.length === 0 && (
              <p className="text-sm text-white/40">{t("studio.first_cheer", "Sois le premier à encourager ! 🙏")}</p>
            )}
            {comments.filter((c) => !c.userId || !blockedIds.has(c.userId)).map((c) => (
              <div key={c.id} className="text-sm leading-snug">
                {c.userId && c.userId !== user?.id ? (
                  <button type="button" className="font-semibold hover:underline" style={{ color: c.isHost ? "#28C291" : "#E48B6E" }}
                    onClick={() => setBlockTarget({ id: c.userId, name: c.name })} title={t("block.action", "Bloquer {name}").replace("{name}", c.name || "")} data-testid={`live-chat-author-${c.id}`}>{c.name}</button>
                ) : (
                  <span className="font-semibold" style={{ color: c.isHost ? "#28C291" : "#E48B6E" }}>{c.name}</span>
                )}
                <span className="ml-1.5 text-white/90">{c.text}</span>
              </div>
            ))}
            <div ref={commentsEndRef} />
          </div>
          <BlockUserDialog open={!!blockTarget} onOpenChange={(o) => { if (!o) setBlockTarget(null); }} user={blockTarget} testidPrefix="live-block" />

          {canOffer && Number(pb.offering_goal) > 0 && showGoal && (
            <div className="mx-3.5 mb-2 rounded-xl px-3 py-2.5 shrink-0" style={{ background: "rgba(217,119,87,0.12)", border: "1px solid rgba(217,119,87,0.3)" }} data-testid="live-offering-goal">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="flex items-center gap-1.5 font-semibold text-white/90"><Target size={13} style={{ color: "#D97757" }} /> {offer.goal}</span>
                <span className="font-bold" style={{ color: "#D97757" }} data-testid="live-offering-goal-amounts">{Math.round(Number(pb.offering_total || 0)).toLocaleString()} {goalSym} / {Math.round(Number(pb.offering_goal)).toLocaleString()} {goalSym}</span>
              </div>
              <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, Math.round((Number(pb.offering_total || 0) / Number(pb.offering_goal)) * 100))}%`, background: "#D97757" }} data-testid="live-offering-goal-fill" />
              </div>
              {Number(pb.offering_total || 0) >= Number(pb.offering_goal) && (
                <div className="text-[11px] mt-1.5 font-semibold" style={{ color: "#28C291" }}>{t("liveroom.goal_reached", "🎉 Objectif atteint — gloire à Dieu !")}</div>
              )}
            </div>
          )}

          {role === "viewer" && canOffer && onDonate && !isOwner && (
            <div className="px-3.5 pb-2 shrink-0">
              <Button
                onClick={onDonate}
                className="w-full rounded-full font-semibold text-white transition-transform duration-300 hover:scale-[1.02]"
                style={{ background: "#D97757", boxShadow: "0 0 20px rgba(217,119,87,0.3)" }}
                data-testid="live-room-donate"
              >
                <Heart size={15} className="mr-1.5" /> {offer.cta}
              </Button>
            </div>
          )}

          {showOfferingCta && (
            <div className="mx-3.5 mb-2 rounded-xl px-3 py-2.5 text-xs flex items-start gap-2 shrink-0" style={{ background: "rgba(32,158,118,0.12)", color: "#28C291", border: "1px solid rgba(32,158,118,0.25)" }} data-testid="live-offering-cta">
              <ShieldCheck size={16} className="shrink-0 mt-0.5" />
              <div>
                {t("live.cta_verify", "Active les {p} — fais vérifier ton compte {role} pour recevoir des {p}.")
                  .replace(/\{p\}/g, ctaOffer.plural)
                  .replace("{role}", ctaRoleLabel || t("live.cta_verify_role_fallback", "(prédicateur, artiste ou influenceur)"))}
                <button onClick={() => nav(`/profile/${user?.id}`)} className="underline font-semibold block mt-1" data-testid="live-verify-account-btn">{t("live.verify_account", "Vérifier mon compte →")}</button>
              </div>
            </div>
          )}

          <div className="p-2.5 border-t flex items-center gap-1.5 shrink-0" style={{ borderColor: "rgba(255,255,255,0.08)", paddingBottom: "calc(0.625rem + var(--sa-bottom))" }} {...profileGateProps(user)} data-testid="live-comment-bar">
            <button type="button" {...keepFocusTap(() => sendComment("🙏 Amen !"))} className="h-9 px-2.5 rounded-full text-sm shrink-0 hover:bg-white/10 transition-colors" title={t("liveroom.send_amen", "Envoyer un Amen")} data-testid="live-amen-btn">🙏</button>
            <input
              className="flex-1 min-w-0 h-9 rounded-full px-3.5 text-sm outline-none text-white placeholder:text-white/40 focus:border-[#28C291]"
              style={{ background: "#0A0D0C", border: "1px solid #273630" }}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendComment()}
              placeholder={t("studio.comment_ph", "Commenter…")}
              maxLength={500}
              data-testid="live-comment-input"
            />
            <button type="button" {...keepFocusTap(() => sendComment())} className="h-9 w-9 rounded-full grid place-items-center shrink-0 transition-transform duration-200 hover:scale-105" style={{ background: "#209E76", color: "#fff" }} data-testid="live-comment-send"><Send size={14} /></button>
          </div>
        </div>
      </div>

      {/* Styled in-app "end the live?" confirmation (replaces the native window.confirm) */}
      {endConfirm && (
        <div className="absolute inset-0 z-[70] grid place-items-center px-6" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} data-testid="live-room-end-confirm">
          <div className="w-full max-w-sm rounded-3xl p-6 text-center" style={{ background: "#14201B", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <div className="mx-auto mb-4 h-14 w-14 rounded-full grid place-items-center" style={{ background: "rgba(220,38,38,0.15)" }}>
              <Radio size={26} style={{ color: "#EF4444" }} />
            </div>
            <h3 className="text-white text-lg font-bold">{t("studio.end_confirm_title", "Terminer le direct ?")}</h3>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">{t("studio.end_confirm_body", "Le direct s'arrêtera et les spectateurs seront redirigés vers l'accueil. Cette action est définitive.")}</p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setEndConfirm(false)}
                disabled={ending}
                className="flex-1 h-11 rounded-full font-semibold text-sm text-white transition-colors"
                style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.28)" }}
                data-testid="live-room-end-cancel"
              >
                {t("studio.cancel", "Annuler")}
              </button>
              <button
                onClick={() => { setEndConfirm(false); endLive(); }}
                disabled={ending}
                className="flex-1 h-11 rounded-full font-semibold text-sm text-white inline-flex items-center justify-center transition-colors"
                style={{ background: "#dc2626" }}
                data-testid="live-room-end-confirm-btn"
              >
                {ending ? <Loader2 size={16} className="animate-spin" /> : t("studio.end_live", "Terminer le direct")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveRoom;
